import * as Sentry from '@sentry/react';

/**
 * Supabase/PostgREST failures arrive as a plain object — `{ code, details,
 * hint, message }` — not an Error.
 *
 * Two consequences, both of which made the comms errors undiagnosable:
 *
 *  - `Sentry.captureException(plainObject)` renders as
 *    "Object captured as exception with keys: code, details, hint, message"
 *    and every field that identifies the cause is discarded.
 *  - `throw plainObject` inside a react-query `queryFn` gives the rejection no
 *    name either, so those issues group under the callback name
 *    ("useQuery$argument_0.queryFn") with nothing to distinguish them.
 *
 * In tenant-admin this is TENANT-ADMIN-PROD-79 (109 events / 46 users) and
 * friends, none of which could be acted on.
 */
export interface SupabaseErrorLike {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

/** Best-effort read of the PostgREST fields off an unknown caught value. */
function fields(error: unknown): SupabaseErrorLike {
  if (!error || typeof error !== 'object') return {};
  const e = error as SupabaseErrorLike;
  return {
    code: typeof e.code === 'string' ? e.code : undefined,
    message: typeof e.message === 'string' ? e.message : undefined,
    details: typeof e.details === 'string' ? e.details : undefined,
    hint: typeof e.hint === 'string' ? e.hint : undefined,
  };
}

/**
 * PostgREST's expired/invalid-JWT signals. Recoverable, not defects.
 *
 * The PGRST3xx family is JWT-specific (docs.postgrest.org "Errors"):
 *
 *   PGRST300  500  server's JWT secret is missing   -> NOT recoverable
 *   PGRST301  401  token could not be decoded       -> re-mint
 *   PGRST302  401  no Bearer sent, anon disabled    -> re-mint
 *   PGRST303  401  claims failed to validate/parse  -> re-mint
 *
 * PGRST303 is where an expired token lands and the only one production emits
 * (TENANT-ADMIN-PROD-8E). It was missing, so recognising it hung on the
 * message happening to contain "jwt". Same list as WorkerAppV2.
 */
const RECOVERABLE_AUTH_CODES = new Set([
  'PGRST301',
  'PGRST302',
  'PGRST303',
  '401',
]);
// Its real message is "Server lacks JWT secret" — which the wording fallback
// below would otherwise match, silently undoing the exclusion.
const SERVER_MISCONFIG_CODES = new Set(['PGRST300']);

export function isSupabaseAuthError(error: unknown): boolean {
  const { code, message } = fields(error);
  if (code && SERVER_MISCONFIG_CODES.has(code)) return false;
  if (code && RECOVERABLE_AUTH_CODES.has(code)) return true;
  const msg = (message ?? '').toLowerCase();
  return msg.includes('jwt') || msg.includes('unauthorized');
}

/**
 * Build a real Error that keeps the payload in both the name and the message,
 * so the Sentry title reads e.g.
 *   SupabaseError(PGRST301): fetch_messages — JWT expired
 *
 * A value that is already an Error is returned untouched — it has a stack, and
 * rewrapping would throw that away.
 */
export function toSupabaseError(error: unknown, action: string): Error {
  if (error instanceof Error) return error;

  const { code, message } = fields(error);
  const err = new Error(`${action} — ${message ?? 'unknown error'}`);
  err.name = `SupabaseError${code ? `(${code})` : ''}`;
  return err;
}

/**
 * Report a Supabase failure with its fields intact and return the Error to
 * throw, so a single call site does both:
 *
 *   if (error) throw captureSupabaseError(error, { action: 'fetch_messages' });
 *
 * An expired/invalid-JWT rejection is reported as a WARNING under one issue
 * per code, not silenced. Captured per query and per stack it produced
 * TENANT-ADMIN-PROD-8E; grouped, it stays one issue. It must not be dropped:
 * tenant-admin has no fetch layer that re-mints and replays on 401 (WorkerAppV2
 * does), so a bad signing key (PGRST301 after a successful mint) or a missing
 * token (PGRST302) would otherwise be a silent chat outage — and PGRST303 is
 * how we see whether the provider's token handout fixed 8E.
 */
export function captureSupabaseError(
  error: unknown,
  { action, extra }: { action: string; extra?: Record<string, unknown> },
): Error {
  const reportable = toSupabaseError(error, action);
  const { code, message, details, hint } = fields(error);
  const authRejected = isSupabaseAuthError(error);
  Sentry.captureException(reportable, {
    ...(authRejected && {
      level: 'warning' as const,
      fingerprint: ['supabase-auth-rejected', code ?? 'no-code'],
    }),
    tags: { feature: 'comms', action },
    extra: {
      ...extra,
      code: code ?? null,
      message: message ?? (error instanceof Error ? error.message : null),
      details: details ?? null,
      hint: hint ?? null,
    },
  });
  return reportable;
}
